// Percorso file: src/pages/home/EnrolNextSeason.jsx
//
// IL RINNOVO VISTO DAL SOCIO (cantiere YS, fetta YS-11.2) — il pulsante
// «Enrol for <anno>» e la lista a spunte della famiglia.
//
// Sta in `pages/home/` e non in `components/` perché lo montano SOLO le tre
// home (coach, parent, player). (`idsDeiFigli`, che un tempo leggeva da
// HomeIncomplete.jsx, qui accanto, ha oggi sede in utils/famiglia.js.)
//
// ⚠️ IL SERVER DECIDE, QUESTA PAGINA CHIEDE. La rotta `POST /renew`
// (server.js) verifica il legame di tutela figlio per figlio, sceglie quali
// ruoli far rinascere e con quale timbro, e rifiuta se le iscrizioni sono
// chiuse. Qui NON si ricalcola niente di tutto questo: si mandano gli id dei
// figli spuntati e si mostra l'esito che torna. L'unica lettura di ruoli che
// si fa è quella descritta più sotto, e serve a NON OFFRIRE ciò che la rotta
// non fa.
//
// QUANDO COMPARE. Due condizioni, entrambe necessarie:
//   1. SOVRAPPOSIZIONE — `enrollmentSeason === operatingSeason + 1`: il club
//      sta già raccogliendo le iscrizioni dell'anno prossimo mentre gioca
//      quello in corso — OPPURE STAGIONE UNICA — `enrollmentSeason ===
//      operatingSeason`, cioè dopo Close season (ottobre 2026, decisione del
//      committente). A stagione unica il rinnovo serve a chi NON ha ancora la
//      tessera dell'anno in corso: la rotta gli dà titolo da una tessera di
//      un anno precedente. Per tutti gli altri l'anno d'iscrizione è già il
//      loro anno, e la riga «confirmed» non si disegna (vedi sotto); per chi è
//      "inAttesa" resta la riga «awaiting approval».
//   2. ISCRIZIONI APERTE — `enrollmentsOpen !== false`. Il confronto è col
//      `false` esplicito, non con la verità dell'espressione: fuori da
//      "ready" quel valore è `true` per difetto (AuthContext), e `undefined`
//      non è una chiusura dichiarata.
// I tre valori arrivano dalla cassettina della stagione, via `useAuth()`
// (AuthContext.js): qui si LEGGONO e basta.
//
// ⚠️ CHI CHIAMA È SEMPRE UN BERSAGLIO, E NON SI PUÒ TOGLIERE. Il corpo di
// `POST /renew` porta i soli id dei FIGLI: l'identità di chi rinnova viene dal
// token, e la rotta la aggiunge da sé. Una spunta su «You» che si potesse
// togliere sarebbe quindi una spunta FINTA — si toglierebbe dallo schermo e
// non dalla richiesta. Per questo la riga di sé stessi c'è, è spuntata, ed è
// SPENTA: dice la verità di ciò che accadrà.
//
// ⚠️ CHI HA GIÀ CHIESTO NON VEDE UN PULSANTE, VEDE UNO STATO (YS-11.3). Da
// quando `POST /renew` scrive `renewedSeason` sui rinnovati, il socio che ha
// già premuto Confirm per l'anno d'iscrizione trova, nella stessa finestra e
// nello stesso posto, una riga di stato — «Renewal request received for
// <anno> — awaiting approval» — invece del pulsante. Non è cliccabile e non
// riapre il pannello, ed è voluto: la domanda è già stata posta e la risposta
// la dà il club approvando le tessere, che nascono PENDING. Il campo si legge
// e basta — chi lo scrive è la rotta.
// ⚙️ D-02, R2.2: la riga non la decide più `renewedSeason` («chiesto», non
// «iscritto») ma il cartellino `statoRinnovo` (AuthContext), cioè lo stato
// della PROPRIA tessera dell'anno d'iscrizione: «awaiting approval» finché è
// PENDING, «confirmed» quando è ACTIVE e completata (vedi sopra la riga).
//
// COSA NON FA — confini della fetta, non omissioni:
//   · non ri-accetta i termini e non rivede l'anagrafica (fette successive);
//   · la riga al posto del pulsante non parla della FAMIGLIA: è lo stato del
//     solo socio collegato, non quello dei figli;
//   · non rimedia al «figlio dimenticato» — chi ha rinnovato sé e un figlio su
//     due non può tornare qui per il secondo, e non è una svista: quel caso
//     lo sistema l'amministrazione, e riaprire il pannello a chi ha già chiesto
//     riproporrebbe l'intera famiglia per un'eccezione rara.

import React, { useEffect, useMemo, useState } from "react";
import { FaUserPlus } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../api/api";
import { canonicalRole } from "../../auth/access";
import { idsDeiFigli } from "../../utils/famiglia";
import "../../components/AdminTools.css";
import "./EnrolNextSeason.css";

// I RUOLI DI ISCRIZIONE che il socio rinnova DA SÉ. È lo stesso elenco di
// `RUOLI_RINNOVABILI` in server.js, e la sede autorevole resta quella: qui
// serve a non OFFRIRE un pulsante a chi la rotta salterebbe.
// ⚠️ COACH C'È da settembre 2026: l'allenatore registrato rinnova la propria
// tessera da qui, e la tessera nasce PENDING come tutte le altre. Restano
// fuori ADMIN e SUPERADMIN, che non si iscrivono e non si rinnovano.
const RUOLI_RINNOVABILI = ["PLAYER", "PARENT", "COACH"];

// L'etichetta del proprio ruolo nella riga «You». Solo i tre rinnovabili: gli
// altri non arrivano mai qui.
const ETICHETTA_RUOLO = { PLAYER: "Player", PARENT: "Parent", COACH: "Coach" };

/** Il figlio di cui si conosce l'id e nient'altro. */
const figlioInAttesa = (id) => ({ id, stato: "caricamento", nome: "" });

/**
 * Il messaggio del server, quando è scritto per essere letto.
 *
 * Le rotte v2 rispondono `{ error: "..." }` e quei testi sono già in inglese e
 * già rivolti al socio (server.js: «Enrollments are currently closed. Please
 * contact the club admin.»). Ricopiarli qui significherebbe tenerne due copie
 * che divergono. Si legge quindi il corpo, e se non è leggibile si ripiega sul
 * testo passato.
 */
const messaggioDelServer = (err, ripiego) => {
  try {
    const corpo = JSON.parse((err && err.body) || "");
    const testo = corpo && typeof corpo.error === "string" ? corpo.error.trim() : "";
    return testo || ripiego;
  } catch {
    return ripiego;
  }
};

/**
 * LA RIGA DEI FIGLI NON INCLUSI (R2.1), oppure stringa vuota.
 *
 * `skipped` è la risposta di `POST /renew`: un elenco di `{ id }` dei bersagli
 * per cui non è nato nulla — soli id, nessun nome, nessun motivo. Qui si dice
 * SOLO CHE MANCANO (decisione del committente), non il perché: il perché lo
 * sa il club.
 *
 * ⚠️ SI NOMINANO SOLO I FIGLI. L'incrocio è con `figli` (le schede lette
 * all'apertura del pannello), e un id che non è fra quelli — chi chiama, per
 * esempio l'amministratore-genitore che la rotta salta sempre — non si nomina.
 * La forma `{ id }` è quella della rotta; un id nudo si accetta lo stesso.
 */
const fraseDeiNonInclusi = (skipped, figli) => {
  if (!Array.isArray(skipped) || skipped.length === 0) return "";

  const idSaltati = skipped.map((voce) =>
    typeof voce === "string" ? voce : voce && voce.id
  );

  const nomi = figli
    .filter((figlio) => idSaltati.includes(figlio.id))
    .map((figlio) => figlio.nome || "A child's record");

  return nomi.length > 0
    ? `The following were not included: ${nomi.join(", ")}. Please contact the club.`
    : "";
};

const EnrolNextSeason = () => {
  const {
    user,
    operatingSeason,
    enrollmentSeason,
    enrollmentsOpen,
    refreshUserFromBackend
  } = useAuth();

  const [aperto, setAperto] = useState(false);
  const [figli, setFigli] = useState([]);
  const [scelti, setScelti] = useState([]);
  const [invio, setInvio] = useState(false);
  const [esito, setEsito] = useState(null);

  // ⚠️ CHIAVE STABILE, non l'oggetto `user` (stessa avvertenza di
  // HomeParent.jsx): `refreshUserFromBackend` restituisce un oggetto
  // nuovo a ogni chiamata, e un effetto che dipendesse da `user` rileggerebbe
  // i figli a ogni giro.
  const idsFigli = useMemo(() => idsDeiFigli(user), [user]);
  const chiaveFigli = idsFigli.join("|");

  // IL PROPRIO RUOLO DI ISCRIZIONE, se è di quelli che si rinnovano da sé —
  // altrimenti stringa vuota. UNO SOLO, ed è il rovescio di com'era: prima si
  // leggevano i ruoli POSSEDUTI (`getActiveRoles`, cioè anche i context), e la
  // riga «You» ne elencava anche tre. Da settembre 2026 la rotta fa nascere una
  // sola tessera, quella del campo `role`: qui si mostra la stessa cosa, perché
  // un elenco più lungo di ciò che nascerà sarebbe una promessa falsa.
  //
  // `canonicalRole` è la sede unica del vocabolario (auth/access.js): un socio
  // storico registrato "Child" è un PLAYER, un "Manager" un COACH.
  //
  // L'anno NON si guarda qui, ed è voluto (invariato): il metro d'anno è del
  // server, e sbagliarlo di un anno qui significherebbe nascondere il rinnovo a
  // chi ne ha diritto. Al massimo si mostra una riga «You» a chi la rotta poi
  // salterà — e il riscontro lo dice.
  const mioRuolo = useMemo(() => {
    const ruolo = canonicalRole(user && user.role);
    return RUOLI_RINNOVABILI.includes(ruolo) ? ruolo : "";
  }, [user]);

  const sovrapposizione =
    Number.isInteger(operatingSeason) &&
    Number.isInteger(enrollmentSeason) &&
    enrollmentSeason === operatingSeason + 1;

  // STAGIONE UNICA (ottobre 2026): l'anno d'iscrizione È l'anno in corso. Il
  // pulsante serve a chi non ha ancora rinnovato; se abbia titolo lo decide il
  // server (vedi sopra: l'anno non si guarda qui).
  const stagioneUnica =
    Number.isInteger(operatingSeason) &&
    Number.isInteger(enrollmentSeason) &&
    enrollmentSeason === operatingSeason;

  const visibile =
    (sovrapposizione || stagioneUnica) && enrollmentsOpen !== false;

  // A CHE PUNTO È IL MIO RINNOVO PER QUEST'ANNO D'ISCRIZIONE? (D-02, R2.2)
  //
  // Lo dice il CARTELLINO `statoRinnovo`, non più `renewedSeason`. Quel campo
  // significa «ho chiesto» e nessun gesto successivo lo cambia: la riga
  // «awaiting approval» restava anche dopo l'Approve e dopo il completamento.
  // Il cartellino lo compone AuthContext (`statoRinnovoDellAnno`) dalla tessera
  // dell'anno d'iscrizione, con lo stesso metro d'anno di prima (`annoDiLista`:
  // 2027 e «2027» sono lo stesso anno):
  //   "inAttesa"              → la riga «awaiting approval» di sempre;
  //   "completato"            → la riga «confirmed» (in sovrapposizione; a stagione unica
  //                             nulla, vedi il contenitore più sotto);
  //   "approvatoDaCompletare" → nulla: il cancello porta già a completare
  //                             (/home/incomplete), e un pulsante qui porterebbe
  //                             solo a «Nothing new to enrol»;
  //   "nessuna"/"archiviata"  → il pulsante, come prima.
  //
  // ⚠️ È IL PROPRIO STATO, e dice del solo `user`: la domanda che questa riga
  // risponde è «a che punto sono io?», non «la mia famiglia è a posto?». Chi
  // manca lo rimette l'amministrazione.
  const statoRinnovo = user && user.statoRinnovo;

  // LE SCHEDE DEI FIGLI, e solo all'APERTURA del pannello. A pannello chiuso
  // non parte nessuna chiamata: il pulsante da solo non ha bisogno dei nomi, e
  // una GET per figlio a ogni caricamento di home sarebbe un costo pagato da
  // tutti per un pannello che quasi nessuno apre.
  // È la stessa via di HomeParent (l'avvertenza in testa a quel file vale
  // identica): `linkedProfiles` per gli id, una `GET /users/:id` per scheda, un
  // esito per figlio.
  // ⚠️ La dipendenza è `chiaveFigli` (una stringa) e non `idsFigli`, che è un
  // array nuovo a ogni giro di `user`.
  useEffect(() => {
    if (!aperto) return undefined;

    if (idsFigli.length === 0) {
      setFigli([]);
      setScelti([]);
      return undefined;
    }

    let annullato = false;

    setFigli(idsFigli.map(figlioInAttesa));

    Promise.all(
      idsFigli.map(async (id) => {
        try {
          const documento = await api.get(`/users/${encodeURIComponent(id)}`);
          const d = documento || {};
          const nome = [d.firstName, d.lastName].filter(Boolean).join(" ").trim();
          return { id, stato: "ok", nome };
        } catch {
          // 403 (nessun legame di ritorno), 404 (documento sparito) o guasto
          // di rete: per questo pannello sono lo stesso caso — quel figlio non
          // si può spuntare, e la riga lo dice invece di sparire.
          return { id, stato: "errore", nome: "" };
        }
      })
    ).then((esiti) => {
      if (annullato) return;
      setFigli(esiti);
      // DI DEFAULT SONO TUTTI SPUNTATI, e non è una scorciatoia: il gesto del
      // genitore è «rinnovo la famiglia», e l'esclusione è il caso raro. Le
      // tessere che nascono sono PENDING — non attivano nulla e le approva il
      // club — quindi una spunta di troppo si ripara, mentre un pannello con
      // tutte le spunte vuote fa premere «Confirm» a vuoto e non si capisce
      // perché non sia successo niente.
      // ⚠️ I figli in ERRORE restano FUORI dalla selezione: la loro scheda non
      // si è letta, e mandarne l'id farebbe rispondere 403 alla rotta — che
      // rifiuta in blocco, portandosi via anche i fratelli che avevano diritto.
      setScelti(esiti.filter((f) => f.stato === "ok").map((f) => f.id));
    });

    return () => {
      annullato = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aperto, chiaveFigli]);

  const apri = () => {
    setEsito(null);
    setAperto(true);
  };

  const chiudi = () => {
    if (invio) return;
    setAperto(false);
  };

  const spunta = (id) => {
    setScelti((elenco) =>
      elenco.includes(id) ? elenco.filter((v) => v !== id) : elenco.concat(id)
    );
  };

  const conferma = async () => {
    setInvio(true);
    setEsito(null);

    try {
      // ⚠️ ELENCO CHIUSO: dal corpo partono SOLO gli id dei figli spuntati. Non
      // il proprio id (viene dal token), non i ruoli, non l'anno, non lo
      // status: che cosa nasca lo decide la rotta.
      const risposta = await api.post("/renew", { children: scelti });

      const nate = Array.isArray(risposta && risposta.renewed)
        ? risposta.renewed
        : [];
      const anno = (risposta && risposta.enrollmentSeason) || enrollmentSeason;

      // `renewed` vuoto NON è un errore: è chi ha già chiesto il rinnovo, o chi
      // non ha tessere rinnovabili. La rotta risponde 200 invece di 201, e qui
      // si distingue guardando l'elenco — `api.post` restituisce il corpo, non
      // il codice.
      setEsito(
        nate.length > 0
          ? {
              tipo: "ok",
              testo: `Enrolment for ${anno} requested for ${nate.length} ${
                nate.length === 1 ? "member" : "members"
              }. The club will review it and confirm.`,
              // Solo nel ramo «nato qualcosa». Il ramo vuoto non elenca i non
              // inclusi: dice solo che non è nato nulla e rimanda al club,
              // senza affermare il perché — `renewed` vuoto vale sia per chi
              // ha già chiesto o è iscritto sia per chi non ha titolo.
              nonInclusi: fraseDeiNonInclusi(risposta && risposta.skipped, figli)
            }
          : {
              tipo: "vuoto",
              testo: `Nothing new to enrol for ${anno}. If you think this is wrong, please contact the club.`
            }
      );

      // ⚠️ IL PROPRIO PROFILO SI RILEGGE, SUBITO (fetta RENEW-01, intervento 1).
      // `POST /renew` ha appena fatto nascere la tessera PENDING, ed è quella
      // che il cartellino `statoRinnovo` legge: senza questa rilettura l'utente in
      // memoria resterebbe quello dell'ingresso, il pulsante tornerebbe
      // cliccabile alla chiusura del pannello e solo un ricaricamento della
      // pagina mostrerebbe «Renewal request received». Si passa dalla sede unica
      // (`refreshUserFromBackend` in AuthContext), la stessa che usano
      // MemberProfileModal ed EditMemberPage dopo un salvataggio della propria
      // scheda: è una `GET /users?uid=…`, NON una seconda `POST /renew`.
      //
      // ⚠️ STA NEL RAMO RIUSCITO, e solo lì: una chiamata fallita non ha scritto
      // nulla, e rileggere il profilo dopo un errore direbbe «requested» a chi
      // non ha chiesto niente. Vale anche per la risposta VUOTA (200 con
      // `renewed: []`), che è pur sempre una risposta del server: se il campo
      // c'era già, è giusto che il pulsante diventi lo stato.
      //
      // ⚠️ IL SUO GUASTO NON È IL GUASTO DEL RINNOVO. La `try` esterna finirebbe
      // nel ramo d'errore e mostrerebbe un rifiuto per un rinnovo andato a buon
      // fine: il peggio che può accadere qui è restare sull'utente di prima,
      // cioè esattamente com'era prima di questa fetta.
      if (refreshUserFromBackend) {
        try {
          await refreshUserFromBackend();
        } catch {
          /* utente stantìo: il rinnovo è comunque riuscito */
        }
      }
    } catch (err) {
      const stato = err && err.status;

      let testo;
      if (stato === 409) {
        // I DUE 409 della rotta — iscrizioni chiuse, oppure stagione
        // illeggibile — hanno testi diversi, e la differenza conta per chi
        // legge: uno passa da solo, l'altro no. Li dice il server.
        testo = messaggioDelServer(
          err,
          "Enrolment is not possible right now. Please try again in a moment."
        );
      } else if (stato === 403) {
        testo =
          "You can only enrol yourself and your own children. Please contact the club.";
      } else if (stato === 400) {
        testo = "This request could not be sent. Please contact the club.";
      } else if (!stato) {
        // Nessuno `status`: nessuna risposta è mai arrivata. È la linea, non un
        // rifiuto del club, e dirlo evita di mandare in segreteria chi ha solo
        // il telefono senza campo.
        testo =
          "Could not reach the club. Please check your connection and try again.";
      } else {
        testo = "Something went wrong. Please try again in a moment.";
      }

      setEsito({ tipo: "errore", testo });
    } finally {
      setInvio(false);
    }
  };

  // ⚠️ IL RETURN CONDIZIONALE STA DOPO TUTTI GLI HOOK, mai prima: uscire sopra
  // cambierebbe il numero di hook fra un render e l'altro.
  if (!visibile) return null;

  // Niente da offrire: ruolo di iscrizione non rinnovabile e nessun figlio. È
  // l'amministratore senza figli (vedi RUOLI_RINNOVABILI), e un pulsante che
  // non può far nascere nulla è peggio di nessun pulsante.
  if (!mioRuolo && idsFigli.length === 0) return null;

  const figliVisibili = figli.length > 0 ? figli : idsFigli.map(figlioInAttesa);
  const inAttesa = figliVisibili.some((f) => f.stato === "caricamento");
  const concluso = Boolean(esito) && esito.tipo !== "errore";

  return (
    <>
      {/* IL PULSANTE, oppure LO STATO AL SUO POSTO (YS-11.3). Stesso
          contenitore e stesso posto: è il pulsante che diventa uno stato, non
          una riga nuova che compare da un'altra parte.

          ⚠️ È UN TERNARIO E NON PIÙ UN `return` ANTICIPATO (fetta RENEW-01,
          intervento 1), e la ragione è il pannello. Da quando `conferma`
          rilegge il profilo (`refreshUserFromBackend`), il cartellino diventa
          "inAttesa" mentre il pannello è ancora aperto sul riscontro: con
          l'uscita anticipata il componente si sarebbe smontato di colpo,
          portandosi via la frase che dice al socio com'è andata. Così invece
          il pulsante diventa lo stato SUBITO — è quello che si voleva — e chi
          ha appena confermato legge comunque l'esito e chiude quando ha finito.

          ⚙️ D-02, R2.2: la scelta la fa `statoRinnovo` (vedi sopra), su tre
          vie — «awaiting approval», «confirmed», pulsante — e una quarta senza
          nulla: "approvatoDaCompletare", dove il contenitore stesso non si
          disegna (un `qpsc-menu` vuoto lascerebbe il suo spazio). Il pannello
          qui sotto non dipende da questa scelta.

          ⚙️ STAGIONE UNICA (ottobre 2026): lo stesso trattamento tocca anche a
          "completato". L'anno d'iscrizione è quello in corso, quindi ogni
          socio a posto sarebbe "completato" e leggerebbe in permanenza «Your
          enrolment … is confirmed»: è la normalità, non una notizia. Restano
          il pulsante ("nessuna"/"archiviata"), con sopra la riga «You are not
          enrolled for <anno>», e «awaiting approval» ("inAttesa"). In
          sovrapposizione `stagioneUnica` è falso e nulla di questo cambia.

          ⚠️ LO STATO NON È UN PULSANTE SPENTO, è testo: un `<button disabled>`
          inviterebbe a premere e non spiegherebbe nulla, e un pulsante spento
          in cima alla home sembrerebbe un guasto. Nessun `onClick`, nessun
          pannello da riaprire (vedi l'avvertenza in testa al file).

          IL PULSANTE è il pulsante oro normale dell'app — l'accento
          (`.qpsc-btn-gold--accent`, fondo rosso) è già speso dallo Shop su
          tutte e tre le home, e il doc 07 §5.1 ne ammette UNO per pagina.
          L'evidenza qui è il BORDO, che passa dal rosso di serie al bianco
          caldo `--qpsc-text` — lo stesso gettone che il foglio usa già come
          bordo sulla variante d'accento, quindi nessun colore nuovo — più
          l'etichetta, che porta l'ANNO invece di un generico «next season».
          Sta in un contenitore proprio e NON in un `<nav>`: apre un pannello
          nella stessa pagina, non porta da nessuna parte. */}
      {statoRinnovo !== "approvatoDaCompletare" &&
        !(stagioneUnica && statoRinnovo === "completato") && (
        <div className="qpsc-menu">
          {statoRinnovo === "inAttesa" ? (
            <p className="qpsc-renew__state">
              Renewal request received for {enrollmentSeason} &mdash; awaiting
              approval
            </p>
          ) : statoRinnovo === "completato" ? (
            <p className="qpsc-renew__state">
              Your enrolment for {enrollmentSeason} is confirmed
            </p>
          ) : (
            <>
              {stagioneUnica && (
                <p className="qpsc-renew__state qpsc-renew__state--notice">
                  You are not enrolled for {enrollmentSeason}.
                </p>
              )}
              <button
                type="button"
                className="qpsc-btn-gold qpsc-renew__btn"
                onClick={apri}
              >
                <span className="qpsc-btn-gold__icon">
                  <FaUserPlus />
                </span>
                Renew enrolment for {enrollmentSeason}
              </button>
            </>
          )}
        </div>
      )}

      {aperto && (
        <div className="qpsc-modal-overlay">
          <div
            className="qpsc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="qpsc-renew-title"
          >
            <h2 className="qpsc-modal__title" id="qpsc-renew-title">
              Renew enrolment for {enrollmentSeason}
            </h2>

            <div className="qpsc-modal__body">
              {concluso ? (
                // RISCONTRO. Il pannello resta aperto e la lista sparisce: chi
                // ha appena confermato deve leggere l'esito, non rivedere le
                // spunte che potrebbe premere una seconda volta.
                <>
                  <p
                    className={`qpsc-renew__note${
                      esito.tipo === "ok" ? " qpsc-renew__note--ok" : ""
                    }`}
                  >
                    {esito.testo}
                  </p>
                  {esito.nonInclusi && (
                    <p className="qpsc-renew__note">{esito.nonInclusi}</p>
                  )}
                </>
              ) : (
                <>
                  {/* SÉ STESSI. La spunta è accesa e SPENTA: chi chiama è
                      sempre un bersaglio della rotta e non si può togliere
                      (vedi l'avvertenza in testa al file). Se il proprio ruolo
                      di iscrizione non si rinnova da qui — l'amministratore —
                      non si mostra nessuna spunta, ma una riga che dice
                      perché. */}
                  {mioRuolo ? (
                    <label className="qpsc-check qpsc-check--disabled">
                      <input
                        className="qpsc-check__input"
                        type="checkbox"
                        checked
                        readOnly
                        disabled
                      />
                      <span className="qpsc-check__box" aria-hidden="true" />
                      <span>
                        You &mdash; {ETICHETTA_RUOLO[mioRuolo] || mioRuolo}
                      </span>
                    </label>
                  ) : (
                    <p className="qpsc-renew__note">
                      Your own membership is not renewed from here. You can
                      enrol your children below.
                    </p>
                  )}

                  {/* I FIGLI, uno per riga. Quello la cui scheda non si è letta
                      resta a schermo, spento e senza spunta: farlo sparire
                      lascerebbe il genitore a contare i figli. */}
                  {figliVisibili.map((figlio) => {
                    if (figlio.stato === "caricamento") {
                      return (
                        <p className="qpsc-renew__note" key={figlio.id}>
                          Loading&hellip;
                        </p>
                      );
                    }

                    if (figlio.stato === "errore") {
                      return (
                        <p className="qpsc-renew__note" key={figlio.id}>
                          A child&rsquo;s record could not be loaded, so it
                          cannot be enrolled here. Please contact the club.
                        </p>
                      );
                    }

                    return (
                      <label className="qpsc-check" key={figlio.id}>
                        <input
                          className="qpsc-check__input"
                          type="checkbox"
                          checked={scelti.includes(figlio.id)}
                          onChange={() => spunta(figlio.id)}
                          disabled={invio}
                        />
                        <span className="qpsc-check__box" aria-hidden="true" />
                        <span>{figlio.nome || "A child's record"}</span>
                      </label>
                    );
                  })}

                  <p className="qpsc-renew__note">
                    Your own membership is always included. Enrolments start as
                    requests: the club reviews and confirms them.
                  </p>

                  {esito && esito.tipo === "errore" && (
                    <p className="qpsc-renew__note qpsc-renew__note--error">
                      {esito.testo}
                    </p>
                  )}
                </>
              )}
            </div>

            <div className="qpsc-modal__actions">
              {!concluso && (
                <button
                  type="button"
                  className="qpsc-tool-btn"
                  onClick={conferma}
                  disabled={invio || inAttesa}
                >
                  {invio ? "Sending…" : "Confirm"}
                </button>
              )}
              <button
                type="button"
                className="qpsc-ghost-btn"
                onClick={chiudi}
                disabled={invio}
              >
                {concluso ? "Close" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EnrolNextSeason;
